import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authService, sessionPolicyService } from '../../services';
import { onSessionEnded } from '../../services/api-client';
import {
  IDLE_SIGN_OUT_NOTICE,
  leaveSignOutNotice,
  takeSignOutNotice,
} from '../../lib/sign-out-notice';
import { useAuthStore } from '../../stores';

/**
 * Signs the user out after the organisation's idle timeout.
 *
 * The timeout is a company setting (Settings → Session), read after sign-in
 * from an endpoint every signed-in user may call — it is the rule applied to
 * them, not configuration they manage.
 *
 * Activity is recorded in localStorage rather than in memory so that working
 * in one tab keeps the others signed in: a console open in three tabs is one
 * person, and signing them out of the tab they are not looking at while they
 * type in another would be wrong. Storage being unavailable (private mode,
 * blocked site data) degrades to per-tab tracking, which still enforces the
 * timeout.
 *
 * The check runs on an interval and again whenever the tab becomes visible,
 * because timers are throttled in background tabs and do not run at all while
 * a laptop sleeps — the moment the lid opens is exactly when an expired
 * session must end.
 *
 * The server applies the same timeout to a console that is not open to check
 * it — closed, asleep, or on another computer: it refuses the refresh once the
 * session has been idle past the timeout. When that refusal is what ends the
 * session, this component signs the console out with the same explanation.
 */

const ACTIVITY_KEY = 'micarahr.lastActivityAt';
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;
const CHECK_EVERY_MS = 15_000;
/** Writes are throttled: pointermove fires far more often than this matters. */
const RECORD_EVERY_MS = 5_000;

let memoryLastActivity = Date.now();

function readLastActivity(): number {
  try {
    const stored = Number(window.localStorage.getItem(ACTIVITY_KEY));
    if (Number.isFinite(stored) && stored > 0) return Math.max(stored, memoryLastActivity);
  } catch {
    // Storage blocked: the in-memory value is all there is.
  }
  return memoryLastActivity;
}

function recordActivity(now: number) {
  memoryLastActivity = now;
  try {
    window.localStorage.setItem(ACTIVITY_KEY, String(now));
  } catch {
    // Storage blocked: tracking continues in memory for this tab.
  }
}

// Re-exported: the sign-in page reads the notice from here.
export { takeSignOutNotice };

/**
 * How often, at most, working in the console tells the server it is in use.
 *
 * The server ends a web session that has made no request for longer than the
 * idle timeout (plus a minute's grace), so somebody typing into a long form
 * without saving would otherwise be signed out mid-sentence. Once a minute
 * keeps the server's idea of "last activity" within the grace of this tab's.
 */
const KEEP_ALIVE_EVERY_MS = 60_000;

export function IdleSignOut() {
  const session = useAuthStore((state) => state.session);
  const setSession = useAuthStore((state) => state.setSession);
  const queryClient = useQueryClient();

  const policy = useQuery({
    queryKey: ['session-policy', session?.userId ?? null],
    queryFn: () => sessionPolicyService.get(),
    enabled: Boolean(session),
    // A changed timeout reaches open consoles within the hour without a reload.
    staleTime: 5 * 60_000,
    refetchInterval: 60 * 60_000,
  });

  const minutes = policy.data?.idleTimeoutMinutes;

  // The server ended the session for inactivity (a refused refresh): sign the
  // console out and say why, rather than leaving every request to fail.
  useEffect(() => {
    if (!session) return;
    return onSessionEnded((reason, message) => {
      if (reason !== 'idle_timeout') return;
      leaveSignOutNotice(message ?? IDLE_SIGN_OUT_NOTICE);
      setSession(null);
      queryClient.clear();
    });
  }, [session, setSession, queryClient]);

  useEffect(() => {
    if (!session || !minutes) return;
    const limitMs = minutes * 60_000;

    // Signing in is activity: a stale value from a previous session in this
    // browser must not sign the new one out immediately.
    recordActivity(Date.now());

    let lastWrite = 0;
    let lastKeepAlive = Date.now();
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite < RECORD_EVERY_MS) return;
      lastWrite = now;
      recordActivity(now);
      if (now - lastKeepAlive >= KEEP_ALIVE_EVERY_MS) {
        lastKeepAlive = now;
        void sessionPolicyService.keepAlive().catch(() => undefined);
      }
    };

    let ended = false;
    const check = async () => {
      if (ended || Date.now() - readLastActivity() < limitMs) return;
      ended = true;
      leaveSignOutNotice(
        `You were signed out after ${minutes} minutes without activity. Sign in again to continue.`,
      );
      try {
        await authService.signOut(session);
      } finally {
        setSession(null);
        // Same reason as the menu's sign-out: the cache belongs to the account
        // that filled it.
        queryClient.clear();
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);

    return () => {
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, onActivity);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [session, minutes, setSession, queryClient]);

  return null;
}
